/**
 * Display name for a chat message's sender.
 *
 * Messages carry no name — only `sender_id` (and sometimes `resolved_user_id`)
 * plus `sender_role`. Names live in two side channels, and neither is reliable
 * on its own:
 *
 *  - `participants[]` from `/rooms/{id}/participants`, which carries a `name`
 *    but does not guarantee one.
 *  - the shared `useCommsIdentity` cache, reached here as `resolveName`.
 *
 * Falling back to the bare literal "Coach" — which is what `MessageBubble` did
 * before this module existed — makes every unnamed coach look like the same
 * person. This walks both channels before giving up, and never surfaces a raw
 * id.
 *
 * Not to be confused with `resolveSenderName` in `hooks/comms/CommsNotifyProvider`,
 * which answers a different question (what to title an OS notification).
 */
import { participantId, senderId } from "./commsParticipantId";

/**
 * Last-resort label when an id can't be resolved to a real name — never leak a
 * raw id into the UI.
 * @param {string} [role]
 * @returns {string}
 */
export const unresolvedLabel = (role) => (role === "CLIENT" ? "Client" : "Coach");

/**
 * Trimmed string, or "" for anything else.
 * @param {*} value
 * @returns {string}
 */
const clean = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : "";

/**
 * Resolve the display name to show against a message.
 *
 * Precedence, first non-empty wins:
 *  1. the viewer's own name, for the viewer's own messages
 *  2. the participant record's server-supplied `name`
 *  3. the shared identity cache, tried against BOTH id forms — ms-agents can
 *     put a coach's `employee_id` on `sender_id`, and the cache is keyed by
 *     `user_id` and `employee_id` alike, so one form missing doesn't mean the
 *     other is
 *  4. a role label ("Coach" / "Client")
 *
 * @param {Object} args
 * @param {Object} args.message - Raw message.
 * @param {Array<Object>} [args.participants=[]] - Room participant records.
 * @param {(userId: string, role: string) => string|undefined} [args.resolveName] -
 *   Shared identity-cache lookup (`Thread` passes one built from `useCommsIdentity`).
 * @param {boolean} [args.isOwn=false] - True when the viewer sent this message.
 * @param {string} [args.viewerName] - The viewer's own display name.
 * @returns {string}
 */
export function senderDisplayName({
  message,
  participants = [],
  resolveName,
  isOwn = false,
  viewerName,
}) {
  const role = message?.sender_role;

  if (isOwn) {
    const own = clean(viewerName);
    if (own) return own;
  }

  const normalizedId = senderId(message);
  const participant = participants.find(
    (p) => participantId(p) === normalizedId
  );
  const fromParticipant = clean(participant?.name);
  if (fromParticipant) return fromParticipant;

  if (resolveName) {
    const lookupRole = participant?.participant_role || role;
    // Both id forms: whichever one the cache happens to hold wins.
    for (const id of [normalizedId, message?.sender_id]) {
      if (id == null) continue;
      const resolved = clean(resolveName(id, lookupRole));
      if (resolved) return resolved;
    }
  }

  return unresolvedLabel(participant?.participant_role || role);
}
